import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Grid,
  Box,
  Card,
  CardContent,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Alert,
  Button,
} from "@mui/material";
import {
  TrendingUp,
  People,
  VideoLibrary,
  Visibility,
  ThumbUp,
  Share,
  Comment,
  Analytics as AnalyticsIcon,
  BarChart,
  PieChart,
  Refresh,
} from "@mui/icons-material";
import { YOUR_YOUTUBE_API_KEY } from "../../../Assets/Secretkeys/Secretkey";

// Replace with your actual YouTube API Key and Channel ID
const YOUTUBE_API_KEY = YOUR_YOUTUBE_API_KEY;
const CHANNEL_ID = "UCArm_mYFVEdoZutPXjvbbiA"; // Your YouTube channel ID

const Analytics = ({ user }) => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchYouTubeData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch channel statistics
      const channelResponse = await fetch(
        `https://www.googleapis.com/youtube/v3/channels?part=statistics,snippet&id=${CHANNEL_ID}&key=${YOUTUBE_API_KEY}`
      );

      if (!channelResponse.ok) {
        throw new Error("Failed to fetch channel data");
      }

      const channelData = await channelResponse.json();

      if (!channelData.items || channelData.items.length === 0) {
        throw new Error("Channel not found");
      }

      const channelStats = channelData.items[0].statistics;
      const channelInfo = channelData.items[0].snippet;

      // Fetch recent videos
      const videosResponse = await fetch(
        `https://www.googleapis.com/youtube/v3/search?part=snippet&channelId=${CHANNEL_ID}&maxResults=10&order=date&type=video&key=${YOUTUBE_API_KEY}`
      );

      if (!videosResponse.ok) {
        throw new Error("Failed to fetch videos data");
      }

      const videosData = await videosResponse.json();

      // Fetch detailed video statistics for each video
      const videoIds = videosData.items
        .map((item) => item.id.videoId)
        .join(",");
      const videoStatsResponse = await fetch(
        `https://www.googleapis.com/youtube/v3/videos?part=statistics&id=${videoIds}&key=${YOUTUBE_API_KEY}`
      );

      const videoStatsData = await videoStatsResponse.json();

      // Combine video data with statistics
      const videosWithStats = videosData.items.map((item, index) => ({
        id: item.id.videoId,
        title: item.snippet.title,
        description: item.snippet.description,
        thumbnail: item.snippet.thumbnails.medium.url,
        publishedAt: item.snippet.publishedAt,
        statistics: videoStatsData.items[index]?.statistics || {},
      }));

      setAnalyticsData({
        channelStats,
        channelInfo,
        videos: videosWithStats,
      });
    } catch (err) {
      console.error("Error fetching YouTube data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchYouTubeData();
  }, []);

  // Format numbers with commas
  const formatNumber = (num) => {
    return parseInt(num || 0).toLocaleString();
  };

  // Calculate engagement rate
  const calculateEngagementRate = (views, likes, comments) => {
    const totalEngagement = parseInt(likes || 0) + parseInt(comments || 0);
    const engagementRate =
      (totalEngagement / Math.max(parseInt(views || 1), 1)) * 100;
    return engagementRate.toFixed(1);
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading YouTube Analytics...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={fetchYouTubeData}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  if (!analyticsData) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No analytics data available.
      </Alert>
    );
  }

  const { channelStats, channelInfo, videos } = analyticsData;

  return (
    <Paper
      sx={{
        p: 4,
        minHeight: "100vh",
        // background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Box>
          <Typography
            variant="h3"
            gutterBottom
            sx={{
              // color: "white",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <AnalyticsIcon sx={{ fontSize: 40 }} />
            YouTube Analytics
          </Typography>
          <Typography variant="h6" sx={{ color: "rgba(24, 19, 19, 0.8)" }}>
            {channelInfo?.title || "Channel"} • Welcome back,{" "}
            {user.displayName || user.email}!
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Refresh />}
          onClick={fetchYouTubeData}
          // sx={{ background: "rgba(4, 4, 4, 0.2)", color: "white" }}
        >
          Refresh
        </Button>
      </Box>

      {/* Main Analytics Grid */}
      <Grid container spacing={3}>
        {/* Key Metrics Row */}
        <Grid item xs={12}>
          <Grid container spacing={3}>
            {/* Total Views */}
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  background: "rgba(255,255,255,0.95)",
                  backdropFilter: "blur(10px)",
                }}
              >
                <CardContent sx={{ textAlign: "center", p: 3 }}>
                  <Visibility sx={{ fontSize: 40, color: "#1976d2", mb: 1 }} />
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: "bold", color: "#1976d2" }}
                  >
                    {formatNumber(channelStats.viewCount)}
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    Total Views
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Subscribers */}
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  background: "rgba(255,255,255,0.95)",
                  backdropFilter: "blur(10px)",
                }}
              >
                <CardContent sx={{ textAlign: "center", p: 3 }}>
                  <People sx={{ fontSize: 40, color: "#2e7d32", mb: 1 }} />
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: "bold", color: "#2e7d32" }}
                  >
                    {formatNumber(channelStats.subscriberCount)}
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    Subscribers
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Video Count */}
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  background: "rgba(255,255,255,0.95)",
                  backdropFilter: "blur(10px)",
                }}
              >
                <CardContent sx={{ textAlign: "center", p: 3 }}>
                  <VideoLibrary
                    sx={{ fontSize: 40, color: "#ed6c02", mb: 1 }}
                  />
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: "bold", color: "#ed6c02" }}
                  >
                    {formatNumber(channelStats.videoCount)}
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    Total Videos
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Engagement Rate */}
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  background: "rgba(255,255,255,0.95)",
                  backdropFilter: "blur(10px)",
                }}
              >
                <CardContent sx={{ textAlign: "center", p: 3 }}>
                  <TrendingUp sx={{ fontSize: 40, color: "#9c27b0", mb: 1 }} />
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: "bold", color: "#9c27b0" }}
                  >
                    {calculateEngagementRate(
                      channelStats.viewCount,
                      channelStats.likeCount,
                      channelStats.commentCount
                    )}
                    %
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    Engagement Rate
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* Recent Videos Performance */}
        <Grid item xs={12} md={8}>
          <Card
            sx={{
              background: "rgba(255,255,255,0.95)",
              backdropFilter: "blur(10px)",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="h5"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <BarChart /> Recent Videos Performance
              </Typography>

              {videos.slice(0, 5).map((video, index) => (
                <Box
                  key={video.id}
                  sx={{
                    mb: 3,
                    p: 2,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 1, fontSize: "1rem" }}>
                    {video.title}
                  </Typography>

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6} sm={3}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Visibility sx={{ fontSize: 16, color: "#1976d2" }} />
                        <Typography variant="body2">
                          {formatNumber(video.statistics.viewCount)} views
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <ThumbUp sx={{ fontSize: 16, color: "#2e7d32" }} />
                        <Typography variant="body2">
                          {formatNumber(video.statistics.likeCount)} likes
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Comment sx={{ fontSize: 16, color: "#ed6c02" }} />
                        <Typography variant="body2">
                          {formatNumber(video.statistics.commentCount)} comments
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(video.publishedAt)}
                      </Typography>
                    </Grid>
                  </Grid>

                  {/* Engagement Progress */}
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(
                      (parseInt(video.statistics.viewCount) / 10000) * 100,
                      100
                    )}
                    sx={{ height: 6, borderRadius: 3 }}
                    color="primary"
                  />
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Stats */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              background: "rgba(255,255,255,0.95)",
              backdropFilter: "blur(10px)",
              height: "100%",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="h5"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <PieChart /> Quick Stats
              </Typography>

              <List>
                <ListItem>
                  <ListItemIcon>
                    <ThumbUp color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Total Likes"
                    secondary={formatNumber(channelStats.likeCount)}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Comment color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Total Comments"
                    secondary={formatNumber(channelStats.commentCount)}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Share color="warning" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Avg. Engagement"
                    secondary={`${calculateEngagementRate(
                      channelStats.viewCount,
                      channelStats.likeCount,
                      channelStats.commentCount
                    )}%`}
                  />
                </ListItem>
              </List>

              <Box sx={{ mt: 2, p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Last updated: {new Date().toLocaleString()}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default Analytics;
